import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import CharacterListItem from './components/CharacterListItem';
import CharacterStats from './components/CharacterStats';
import ProgressionOverview from './components/ProgressionOverview';
import RecentAchievements from './components/RecentAchievements';
import RareMobsTracker from './components/RareMobsTracker';
import AddCharacterModal from './components/AddCharacterModal';
import EditCharacterModal from './components/EditCharacterModal';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';

const CharacterManagement = () => {
  const navigate = useNavigate();

  const [characters, setCharacters] = useState([
    {
      id: 1,
      name: 'Thaldrin',
      class: 'Warrior',
      level: 60,
      progression: 85,
      isMain: true,
      guildNotes: 'Main tank for raid progression',
      achievements: {
        total: 147,
        raids: 23,
        quests: 89
      },
      daysActive: 342,
      progressionData: [
        {
          name: 'Buried Sea Missions',
          completed: 12,
          total: 15,
          completion: 80,
          lastUpdate: '2026-02-20T14:30:00'
        },
        {
          name: 'Raid Encounters',
          completed: 8,
          total: 10,
          completion: 80,
          lastUpdate: '2026-02-22T19:45:00'
        },
        {
          name: 'Epic Quests',
          completed: 3,
          total: 4,
          completion: 75,
          lastUpdate: '2026-02-18T11:20:00'
        },
        {
          name: 'Exploration Achievements',
          completed: 18,
          total: 20,
          completion: 90,
          lastUpdate: '2026-02-21T16:15:00'
        }
      ],
      rareMobs: [
        { id: 1, name: 'Siren Queen Tiala', zone: 'Siren\'s Grotto', location: 'Central Chamber', defeated: true, lastDefeated: '2026-02-20T14:30:00' },
        { id: 2, name: 'Blackwater Leviathan', zone: 'Blackwater Depths', location: 'Abyssal Trench', defeated: true, lastDefeated: '2026-02-18T11:20:00' },
        { id: 3, name: 'Captain Bloodsail', zone: 'Deadbone Reef', location: 'Shipwreck Bay', defeated: false },
        { id: 4, name: 'Tidebreaker Kraken', zone: 'Tidal Rift', location: 'Maelstrom Point', defeated: true, lastDefeated: '2026-02-22T19:45:00' },
        { id: 5, name: 'Coral Golem Ancient', zone: 'Coral Gardens', location: 'Deep Reef', defeated: false },
        { id: 6, name: 'Ghostship Admiral', zone: 'Deadbone Reef', location: 'Phantom Fleet', defeated: false },
        { id: 7, name: 'Abyssal Serpent', zone: 'Blackwater Depths', location: 'Dark Caverns', defeated: true, lastDefeated: '2026-02-21T16:15:00' },
        { id: 8, name: 'Tempest Elemental', zone: 'Tidal Rift', location: 'Storm Peak', defeated: false },
        { id: 9, name: 'Siren Matriarch', zone: 'Siren\'s Grotto', location: 'Throne Room', defeated: false },
        { id: 10, name: 'Reef Guardian', zone: 'Coral Gardens', location: 'Guardian\'s Sanctum', defeated: false }
      ],
      recentAchievements: [
        {
          title: 'Solteris Conqueror',
          description: 'Defeated all bosses in Solteris, the Throne of Ro',
          type: 'raid',
          points: 50,
          timestamp: '2026-02-22T19:45:00'
        },
        {
          title: 'Master Explorer',
          description: 'Discovered all hidden locations in the Buried Sea',
          type: 'exploration',
          points: 30,
          timestamp: '2026-02-21T16:15:00'
        },
        {
          title: 'Epic Warrior 2.0',
          description: 'Completed the Warrior Epic 2.0 questline',
          type: 'quest',
          points: 100,
          timestamp: '2026-02-18T11:20:00'
        }
      ]
    },
    {
      id: 2,
      name: 'Mystara',
      class: 'Cleric',
      level: 58,
      progression: 72,
      isMain: false,
      guildNotes: 'Backup healer for raids',
      achievements: {
        total: 98,
        raids: 15,
        quests: 67
      },
      daysActive: 215,
      progressionData: [
        {
          name: 'Buried Sea Missions',
          completed: 10,
          total: 15,
          completion: 67,
          lastUpdate: '2026-02-19T10:30:00'
        },
        {
          name: 'Raid Encounters',
          completed: 6,
          total: 10,
          completion: 60,
          lastUpdate: '2026-02-20T18:20:00'
        },
        {
          name: 'Epic Quests',
          completed: 2,
          total: 4,
          completion: 50,
          lastUpdate: '2026-02-15T14:45:00'
        },
        {
          name: 'Exploration Achievements',
          completed: 15,
          total: 20,
          completion: 75,
          lastUpdate: '2026-02-19T09:10:00'
        }
      ],
      rareMobs: [
        { id: 1, name: 'Siren Queen Tiala', zone: 'Siren\'s Grotto', location: 'Central Chamber', defeated: false },
        { id: 2, name: 'Blackwater Leviathan', zone: 'Blackwater Depths', location: 'Abyssal Trench', defeated: true, lastDefeated: '2026-02-19T10:30:00' },
        { id: 3, name: 'Captain Bloodsail', zone: 'Deadbone Reef', location: 'Shipwreck Bay', defeated: false },
        { id: 4, name: 'Tidebreaker Kraken', zone: 'Tidal Rift', location: 'Maelstrom Point', defeated: false },
        { id: 5, name: 'Coral Golem Ancient', zone: 'Coral Gardens', location: 'Deep Reef', defeated: true, lastDefeated: '2026-02-20T18:20:00' },
        { id: 6, name: 'Ghostship Admiral', zone: 'Deadbone Reef', location: 'Phantom Fleet', defeated: false },
        { id: 7, name: 'Abyssal Serpent', zone: 'Blackwater Depths', location: 'Dark Caverns', defeated: false },
        { id: 8, name: 'Tempest Elemental', zone: 'Tidal Rift', location: 'Storm Peak', defeated: true, lastDefeated: '2026-02-15T14:45:00' },
        { id: 9, name: 'Siren Matriarch', zone: 'Siren\'s Grotto', location: 'Throne Room', defeated: false },
        { id: 10, name: 'Reef Guardian', zone: 'Coral Gardens', location: 'Guardian\'s Sanctum', defeated: false }
      ],
      recentAchievements: [
        {
          title: 'Divine Intervention',
          description: 'Saved the raid from a wipe with clutch healing',
          type: 'raid',
          points: 40,
          timestamp: '2026-02-20T18:20:00'
        },
        {
          title: 'Seafarer',
          description: 'Completed 10 Buried Sea missions',
          type: 'milestone',
          points: 25,
          timestamp: '2026-02-19T10:30:00'
        }
      ]
    },
    {
      id: 3,
      name: 'Shadowbane',
      class: 'Rogue',
      level: 59,
      progression: 68,
      isMain: false,
      guildNotes: 'DPS specialist',
      achievements: {
        total: 112,
        raids: 18,
        quests: 74
      },
      daysActive: 278,
      progressionData: [
        {
          name: 'Buried Sea Missions',
          completed: 9,
          total: 15,
          completion: 60,
          lastUpdate: '2026-02-17T15:40:00'
        },
        {
          name: 'Raid Encounters',
          completed: 7,
          total: 10,
          completion: 70,
          lastUpdate: '2026-02-21T20:30:00'
        },
        {
          name: 'Epic Quests',
          completed: 3,
          total: 4,
          completion: 75,
          lastUpdate: '2026-02-16T12:15:00'
        },
        {
          name: 'Exploration Achievements',
          completed: 12,
          total: 20,
          completion: 60,
          lastUpdate: '2026-02-18T08:25:00'
        }
      ],
      rareMobs: [
        { id: 1, name: 'Siren Queen Tiala', zone: 'Siren\'s Grotto', location: 'Central Chamber', defeated: true, lastDefeated: '2026-02-17T15:40:00' },
        { id: 2, name: 'Blackwater Leviathan', zone: 'Blackwater Depths', location: 'Abyssal Trench', defeated: false },
        { id: 3, name: 'Captain Bloodsail', zone: 'Deadbone Reef', location: 'Shipwreck Bay', defeated: true, lastDefeated: '2026-02-21T20:30:00' },
        { id: 4, name: 'Tidebreaker Kraken', zone: 'Tidal Rift', location: 'Maelstrom Point', defeated: false },
        { id: 5, name: 'Coral Golem Ancient', zone: 'Coral Gardens', location: 'Deep Reef', defeated: false },
        { id: 6, name: 'Ghostship Admiral', zone: 'Deadbone Reef', location: 'Phantom Fleet', defeated: true, lastDefeated: '2026-02-16T12:15:00' },
        { id: 7, name: 'Abyssal Serpent', zone: 'Blackwater Depths', location: 'Dark Caverns', defeated: false },
        { id: 8, name: 'Tempest Elemental', zone: 'Tidal Rift', location: 'Storm Peak', defeated: false },
        { id: 9, name: 'Siren Matriarch', zone: 'Siren\'s Grotto', location: 'Throne Room', defeated: false },
        { id: 10, name: 'Reef Guardian', zone: 'Coral Gardens', location: 'Guardian\'s Sanctum', defeated: false }
      ],
      recentAchievements: [
        {
          title: 'Backstab Master',
          description: 'Achieved 1 million damage in a single raid',
          type: 'raid',
          points: 45,
          timestamp: '2026-02-21T20:30:00'
        },
        {
          title: 'Shadow Walker',
          description: 'Completed Epic Rogue 2.0 questline',
          type: 'quest',
          points: 100,
          timestamp: '2026-02-16T12:15:00'
        }
      ]
    }
  ]);

  const [activeCharacterId, setActiveCharacterId] = useState(1);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const activeCharacter = characters?.find(char => char?.id === activeCharacterId);

  const handleAddCharacter = (newCharacterData) => {
    const newCharacter = {
      id: characters?.length + 1,
      ...newCharacterData,
      progression: 0,
      achievements: {
        total: 0,
        raids: 0,
        quests: 0
      },
      daysActive: 0,
      progressionData: [
        {
          name: 'Buried Sea Missions',
          completed: 0,
          total: 15,
          completion: 0,
          lastUpdate: new Date()?.toISOString()
        },
        {
          name: 'Raid Encounters',
          completed: 0,
          total: 10,
          completion: 0,
          lastUpdate: new Date()?.toISOString()
        },
        {
          name: 'Epic Quests',
          completed: 0,
          total: 4,
          completion: 0,
          lastUpdate: new Date()?.toISOString()
        },
        {
          name: 'Exploration Achievements',
          completed: 0,
          total: 20,
          completion: 0,
          lastUpdate: new Date()?.toISOString()
        }
      ],
      rareMobs: [
        { id: 1, name: 'Siren Queen Tiala', zone: 'Siren\'s Grotto', location: 'Central Chamber', defeated: false },
        { id: 2, name: 'Blackwater Leviathan', zone: 'Blackwater Depths', location: 'Abyssal Trench', defeated: false },
        { id: 3, name: 'Captain Bloodsail', zone: 'Deadbone Reef', location: 'Shipwreck Bay', defeated: false },
        { id: 4, name: 'Tidebreaker Kraken', zone: 'Tidal Rift', location: 'Maelstrom Point', defeated: false },
        { id: 5, name: 'Coral Golem Ancient', zone: 'Coral Gardens', location: 'Deep Reef', defeated: false },
        { id: 6, name: 'Ghostship Admiral', zone: 'Deadbone Reef', location: 'Phantom Fleet', defeated: false },
        { id: 7, name: 'Abyssal Serpent', zone: 'Blackwater Depths', location: 'Dark Caverns', defeated: false },
        { id: 8, name: 'Tempest Elemental', zone: 'Tidal Rift', location: 'Storm Peak', defeated: false },
        { id: 9, name: 'Siren Matriarch', zone: 'Siren\'s Grotto', location: 'Throne Room', defeated: false },
        { id: 10, name: 'Reef Guardian', zone: 'Coral Gardens', location: 'Guardian\'s Sanctum', defeated: false }
      ],
      recentAchievements: []
    };

    setCharacters(prev => [...prev, newCharacter]);
    setActiveCharacterId(newCharacter?.id);
    setIsAddModalOpen(false);
  };

  const handleEditCharacter = (updatedCharacter) => {
    setCharacters(prev => 
      prev?.map(char => char?.id === updatedCharacter?.id ? updatedCharacter : char)
    );
    setIsEditModalOpen(false);
  };

  const handleDeleteCharacter = () => {
    setIsDeleting(true);
    
    setTimeout(() => {
      setCharacters(prev => prev?.filter(char => char?.id !== activeCharacterId));
      
      const remainingCharacters = characters?.filter(char => char?.id !== activeCharacterId);
      if (remainingCharacters?.length > 0) {
        setActiveCharacterId(remainingCharacters?.[0]?.id);
      }
      
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
    }, 1000);
  };

  const handleToggleRareMob = (mobId) => {
    setCharacters(prev => 
      prev?.map(char => {
        if (char?.id === activeCharacterId) {
          return {
            ...char,
            rareMobs: char?.rareMobs?.map(mob => {
              if (mob?.id === mobId) {
                return {
                  ...mob,
                  defeated: !mob?.defeated,
                  lastDefeated: !mob?.defeated ? new Date()?.toISOString() : null
                };
              }
              return mob;
            })
          };
        }
        return char;
      })
    );
  };

  const characterSelectOptions = characters?.map(char => ({
    value: char?.id,
    label: `${char?.name} - Level ${char?.level} ${char?.class}`
  }));

  return (
    <>
      <Helmet>
        <title>Character Management - Maladapted Miscreants</title>
        <meta name="description" content="Manage your EverQuest characters, track progression, and view achievements in the Maladapted Miscreants guild." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-24 pb-12 px-4 md:px-6 lg:px-8">
          <div className="max-w-screen-2xl mx-auto">
            <div className="mb-6 md:mb-8 lg:mb-10">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4">
                Character Management
              </h1>
              <p className="text-sm md:text-base lg:text-lg text-muted-foreground">
                Manage your characters, track progression, and view achievements
              </p>
            </div>

            <div className="mb-6 md:mb-8">
              <Button
                variant="default"
                onClick={() => setIsAddModalOpen(true)}
                iconName="UserPlus"
                iconPosition="left"
                className="w-full sm:w-auto"
              >
                Add New Character
              </Button>
            </div>

            <div className="lg:hidden mb-4 md:mb-6">
              <Select
                label="Select Character"
                options={characterSelectOptions}
                value={activeCharacterId}
                onChange={setActiveCharacterId}
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6 lg:gap-8">
              <div className="hidden lg:block lg:col-span-3">
                <div className="bg-card rounded-xl p-4 md:p-6 border border-border sticky top-24">
                  <div className="flex items-center justify-between mb-4 md:mb-6">
                    <h2 className="text-lg md:text-xl font-semibold">
                      Characters
                    </h2>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setIsAddModalOpen(true)}
                      iconName="Plus"
                    />
                  </div>

                  <div className="space-y-2 md:space-y-3 mb-4 md:mb-6">
                    {characters?.map(character => (
                      <CharacterListItem
                        key={character?.id}
                        character={character}
                        isActive={character?.id === activeCharacterId}
                        onClick={() => setActiveCharacterId(character?.id)}
                      />
                    ))}
                  </div>

                  <Button
                    variant="outline"
                    onClick={() => setIsAddModalOpen(true)}
                    iconName="UserPlus"
                    iconPosition="left"
                    fullWidth
                  >
                    Add Character
                  </Button>
                </div>
              </div>

              <div className="lg:col-span-9 space-y-4 md:space-y-6 lg:space-y-8">
                {activeCharacter && (
                  <>
                    <div className="bg-card rounded-xl p-4 md:p-6 lg:p-8 border border-border">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 md:mb-8">
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold">
                              {activeCharacter?.name}
                            </h2>
                            {activeCharacter?.isMain && (
                              <span className="px-3 py-1 rounded-lg bg-accent/20 text-accent-foreground text-sm font-medium">
                                Main
                              </span>
                            )}
                          </div>
                          <p className="text-sm md:text-base text-muted-foreground">
                            Level {activeCharacter?.level} {activeCharacter?.class}
                          </p>
                        </div>

                        <div className="flex items-center gap-2 md:gap-3">
                          <Button
                            variant="outline"
                            size="default"
                            onClick={() => setIsEditModalOpen(true)}
                            iconName="Edit"
                            iconPosition="left"
                          >
                            Edit
                          </Button>
                          
                          <Button
                            variant="outline"
                            size="default"
                            onClick={() => navigate('/expansion-progression')}
                            iconName="TrendingUp"
                            iconPosition="left"
                          >
                            View Progression
                          </Button>

                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setIsDeleteModalOpen(true)}
                            iconName="Trash2"
                          />
                        </div>
                      </div>

                      {activeCharacter?.guildNotes && (
                        <div className="mb-6 md:mb-8 p-4 rounded-lg bg-muted/50">
                          <div className="flex items-start gap-2 md:gap-3">
                            <Icon name="FileText" size={20} color="var(--color-muted-foreground)" className="flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-xs md:text-sm font-medium text-muted-foreground mb-1">
                                Guild Notes
                              </p>
                              <p className="text-sm md:text-base">
                                {activeCharacter?.guildNotes}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      <CharacterStats character={activeCharacter} />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 lg:gap-8">
                      <ProgressionOverview progressionData={activeCharacter?.progressionData} />
                      <RecentAchievements achievements={activeCharacter?.recentAchievements} />
                    </div>

                    <RareMobsTracker 
                      rareMobs={activeCharacter?.rareMobs} 
                      onToggleMob={handleToggleRareMob}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </main>

        <AddCharacterModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onSave={handleAddCharacter}
        />

        <EditCharacterModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          character={activeCharacter}
          onSave={handleEditCharacter}
        />

        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          character={activeCharacter}
          onConfirm={handleDeleteCharacter}
          isDeleting={isDeleting}
        />
      </div>
    </>
  );
};

export default CharacterManagement;